import { useEffect, useState } from "react";
import { Download, Pause, Play, Trash2 } from "lucide-react";
import type { FileData } from "../data";
import {
  formatBitrate,
  formatBytes,
  formatDuration,
  formatSampleRate,
  probeAudioMeta,
  type AudioMeta,
} from "../lib/audioMeta";
import { getDownloadUrl, getFileUrl, isAudioFormat } from "../lib/media";

interface Props {
  file: FileData;
  isAdmin: boolean;
  onDelete: (fileId: string) => Promise<void>;
  isActive?: boolean;
  isPlaying?: boolean;
  onPlay?: (file: FileData) => void;
  compact?: boolean;
  canDownload?: boolean;
  canDelete?: boolean;
}

export default function FileRow({
  file,
  isAdmin,
  onDelete,
  isActive = false,
  isPlaying = false,
  onPlay,
  compact = false,
  canDownload = true,
  canDelete = true,
}: Props) {
  const [removing, setRemoving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [url, setUrl] = useState<string | undefined>(() => getFileUrl(file));
  const [meta, setMeta] = useState<AudioMeta | null>(null);

  useEffect(() => {
    setUrl(getFileUrl(file));
  }, [file.sourcePath, file.storageProvider, file.deliveryUrl]);

  // Only probe technical details for the active/playing row (avoids decoding every file)
  useEffect(() => {
    if (
      !isActive ||
      !url ||
      file.access !== "available" ||
      !isAudioFormat(file.format, file.title)
    ) {
      return;
    }
    let cancelled = false;
    probeAudioMeta(url)
      .then((next) => {
        if (!cancelled) setMeta(next);
      })
      .catch(() => {
        if (!cancelled) setMeta(null);
      });
    return () => {
      cancelled = true;
    };
  }, [isActive, url, file.access, file.format, file.title]);

  async function handleRemove() {
    if (!window.confirm(`Remove ${file.title} from this project?`)) return;
    setRemoving(true);
    try {
      await onDelete(file.id);
    } finally {
      setRemoving(false);
    }
  }

  if (file.access !== "available") {
    return (
      <div className="file-actions">
        {isAdmin ? (
          <span className={`status-pill ${file.access}`}>{file.access.replaceAll("_", " ")}</span>
        ) : (
          <span className="soft-status">Coming soon</span>
        )}
        {isAdmin && canDelete ? (
          <button className="danger-button" disabled={removing} onClick={handleRemove} type="button">
            <Trash2 className="h-4 w-4" />
            Remove
          </button>
        ) : null}
      </div>
    );
  }

  const isAudio = isAudioFormat(file.format, file.title) && !!url;
  const note = url ? undefined : "Delivery link has not been connected yet";

  return (
    <div className={`file-actions ${compact ? "file-actions-compact" : ""} ${isActive ? "active" : ""}`}>
      <div className="file-action-buttons">
        {onPlay ? (
          <button
            aria-label={`${isPlaying ? "Pause" : "Play"} ${file.title}`}
            disabled={!isAudio}
            onClick={() => onPlay(file)}
            title={isAudio ? "Play file" : note}
            type="button"
            className={`soundcloud-play-button soundcloud-play-button-compact ${isPlaying ? "playing" : ""}`}
          >
            {isPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
          </button>
        ) : (
          <button
            aria-label={showPreview ? `Hide preview for ${file.title}` : `Play ${file.title}`}
            disabled={!isAudio}
            onClick={() => setShowPreview((v) => !v)}
            title={isAudio ? "Preview audio" : note}
            type="button"
            className={`soundcloud-play-button soundcloud-play-button-compact ${showPreview ? "playing" : ""}`}
          >
            {showPreview ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
          </button>
        )}

        {url && canDownload ? (
          <a className="file-action-link" download href={getDownloadUrl(file)} rel="noreferrer" target="_blank">
            <Download className="h-4 w-4" />
            Download
          </a>
        ) : (
          <button
            disabled
            title={url ? "Available after project approval and payment" : note}
            type="button"
          >
            <Download className="h-4 w-4" />
            {url ? "Locked" : "Download"}
          </button>
        )}

        {isAdmin && canDelete ? (
          <button className="danger-button" disabled={removing} onClick={handleRemove} type="button">
            <Trash2 className="h-4 w-4" />
            {removing ? "Removing" : "Remove"}
          </button>
        ) : null}
      </div>

      {!onPlay && showPreview && url ? (
        <audio className="file-preview" controls preload="none" src={url} />
      ) : null}

      {isActive && meta && (meta.durationSec || meta.sizeBytes || meta.sampleRate || meta.bitrateKbps) ? (
        <div className="file-row-meta">
          {formatDuration(meta.durationSec) ? <span>{formatDuration(meta.durationSec)}</span> : null}
          {formatSampleRate(meta.sampleRate) ? <span>{formatSampleRate(meta.sampleRate)}</span> : null}
          {formatBitrate(meta.bitrateKbps) ? <span>{formatBitrate(meta.bitrateKbps)}</span> : null}
          {formatBytes(meta.sizeBytes) ? <span>{formatBytes(meta.sizeBytes)}</span> : null}
        </div>
      ) : null}
    </div>
  );
}
